import { useForm } from '@inertiajs/react';
import { FormEvent, useState } from 'react';
import type { AxialCoord } from '@/lib/hexMath';
import type { TerrainType } from '@/components/HexMap';
import { TERRAIN_COLORS } from '@/components/HexMap';
import { listTilesByDomain, getTileUrl } from '@/components/HexMap/tiles';

interface Props {
    coord: AxialCoord;
    onClose: () => void;
    isGm?: boolean;
    /** Existing hex data if this hex was configured by a GM (has name, domain, tileImage but no owner) */
    existingHex?: { name?: string; domain?: 'deadlands'|'drylands'|'greenlands'|'icelands'|'sandlands'; tileImage?: string };
}

// Terrain options with labels
const TERRAIN_OPTIONS: Array<{ value: TerrainType; label: string }> = [
    { value: 'plains', label: 'Plains' },
    { value: 'forest', label: 'Forest' },
    { value: 'mountain', label: 'Mountain' },
    { value: 'water', label: 'Water' },
    { value: 'swamp', label: 'Swamp' },
    { value: 'desert', label: 'Desert' },
    { value: 'jungle', label: 'Jungle' },
    { value: 'tundra', label: 'Tundra' },
    { value: 'glacier', label: 'Glacier' },
    { value: 'lava', label: 'Lava' },
    { value: 'beach', label: 'Beach' },
    { value: 'city', label: 'City' },
];

/**
 * Modal for claiming a hex with name and terrain selection.
 */
// Decorative corner flourish used on parchment borders
function CornerFlourish({ className = '' }: { className?: string }) {
    return (
        <svg
            className={`w-8 h-8 text-amber-800/30 ${className}`}
            viewBox="0 0 100 100"
            fill="currentColor"
        >
            <path d="M0,0 Q50,10 50,50 Q10,50 0,0 M10,0 Q40,20 40,40 Q20,40 10,0" />
        </svg>
    );
}

// Wax seal for visual flair
function WaxSeal() {
    return (
        <div
            className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-red-700 to-red-900"
            style={{
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.3)',
            }}
        >
            <span className="text-xs font-serif text-amber-100/80">✦</span>
        </div>
    );
}

export function ClaimHexModal({ coord, onClose, isGm = false, existingHex }: Props) {
    // Check if this hex was pre-configured by a GM (has domain and tile set)
    const hasExistingTile = !!(existingHex && existingHex.domain && existingHex.tileImage);

    const { data, setData, post, processing, errors } = useForm({
        q: coord.q,
        r: coord.r,
        // Pre-populate name from existing hex if available
        name: existingHex?.name ?? '',
        // deprecated terrain (kept to satisfy backend default), not submitted
        terrain: 'plains' as TerrainType,
        // Pre-populate domain and tile_image from existing hex if available
        domain: (hasExistingTile ? existingHex!.domain! : '') as '' | 'deadlands' | 'drylands' | 'greenlands' | 'icelands' | 'sandlands',
        tile_image: (hasExistingTile ? existingHex!.tileImage! : '') as string,
        // GM-only fields - for players claiming a configured hex, is_claimable should be false
        is_claimable: isGm ? true : false,
        is_open_hex: false as boolean,
        open_type: 'default' as 'default' | 'enclave',
        default_image: '' as string,
        // Enclave-specific: custom owner display name
        owner_name: '' as string,
    });

    // Start at Step 3 if we have existing tile data (skip domain/tile selection)
    const [step, setStep] = useState<1 | 2 | 3>(hasExistingTile ? 3 : 1);
    // Track whether user wants to keep the existing tile or choose a new one
    const [keepExisting, setKeepExisting] = useState<boolean>(hasExistingTile);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (!data.domain) return;
        if (!data.tile_image) return;
        post('/hex/claim', {
            onSuccess: () => onClose(),
        });
    };

    return (
        // Backdrop - clicking outside the modal closes it
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            {/* Parchment scroll container - stop propagation to prevent closing when clicking inside */}
            <div className="relative w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
                <div
                    className="relative rounded-lg overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, #f5e6d3 0%, #e8d5b7 25%, #f0e2cc 50%, #e5d4b8 75%, #f2e4c9 100%)',
                        boxShadow: '0 6px 24px rgba(0,0,0,0.45), inset 0 0 60px rgba(139,90,43,0.15)'
                    }}
                >
                    {/* Decorative corner flourishes */}
                    <CornerFlourish className="absolute top-2 left-2" />
                    <CornerFlourish className="absolute top-2 right-2 rotate-90" />
                    <CornerFlourish className="absolute bottom-2 left-2 -rotate-90" />
                    <CornerFlourish className="absolute bottom-2 right-2 rotate-180" />

                    {/* Aged paper edge effects */}
                    <div
                        className="absolute top-0 left-0 right-0 h-3 pointer-events-none"
                        style={{ background: 'linear-gradient(to bottom, rgba(139,90,43,0.2), transparent)' }}
                    />
                    <div
                        className="absolute bottom-0 left-0 right-0 h-3 pointer-events-none"
                        style={{ background: 'linear-gradient(to top, rgba(139,90,43,0.2), transparent)' }}
                    />

                    {/* Close button - styled to match parchment theme */}
                    <button
                        type="button"
                        onClick={onClose}
                        className="cursor-pointer absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-amber-200/50 hover:bg-amber-300/70 text-amber-800 hover:text-amber-900 transition-all border border-amber-600/30 hover:border-amber-600/50 shadow-sm"
                        aria-label="Close"
                        title="Close"
                    >
                        <span className="text-lg font-serif leading-none">×</span>
                    </button>

                    {/* Inner content area */}
                    <div className="relative p-6 pt-8">
                        {/* Ornate header */}
                        <div className="text-center mb-5">
                            <div className="inline-flex items-center gap-3 mb-2">
                                <WaxSeal />
                                <h1 className="text-2xl font-serif text-amber-900 tracking-wide">
                                    Stake Your Claim
                                </h1>
                                <WaxSeal />
                            </div>
                            <p className="text-sm font-serif italic text-amber-800/70">
                                By the Charter of Sellune, let deeds be bound to land.
                            </p>
                        </div>

                        {/* Decorative divider */}
                        <div className="flex items-center justify-center gap-2 mb-6">
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                            <span className="text-amber-800/40 text-lg">✦</span>
                            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-800/30 to-transparent" />
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5 font-serif">
                            {/* Name input */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-amber-900 mb-2">
                                    Proclaimed Name
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    value={data.name}
                                    onChange={(e) => setData('name', e.target.value)}
                                    className="w-full px-4 py-3 bg-white/60 border border-amber-400/60 rounded-lg text-amber-900 placeholder-amber-700/40 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    placeholder="Enter the name by which this domain shall be known..."
                                    required
                                    autoFocus
                                />
                                {errors.name && (
                                    <p className="mt-2 text-sm text-red-700">{errors.name}</p>
                                )}
                            </div>

                            {/* Step 1: Domain selection */}
                            {step === 1 && (
                                <div>
                                    <label className="block text-sm font-medium text-amber-900 mb-2">
                                        Select Your Domain
                                    </label>
                                    {hasExistingTile && (
                                        <div className="mb-3 p-3 bg-amber-100/50 rounded-lg border border-amber-400/30">
                                            <p className="text-sm text-amber-900 mb-2">
                                                This hex was configured by a GM with a pre-selected tile.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // Restore the existing tile and go back to Step 3
                                                    setData('domain', existingHex!.domain!);
                                                    setData('tile_image', existingHex!.tileImage!);
                                                    setKeepExisting(true);
                                                    setStep(3);
                                                }}
                                                className="text-sm text-amber-800 hover:text-amber-900"
                                            >
                                                ← Keep existing tile
                                            </button>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {(['deadlands','drylands','greenlands','icelands','sandlands'] as const).map((d) => (
                                            <button
                                                key={d}
                                                type="button"
                                                onClick={() => {
                                                    setData('domain', d);
                                                    setKeepExisting(false);
                                                    setStep(2);
                                                }}
                                                className={`p-3 rounded-lg border transition-colors bg-white/50 hover:bg-white/70 ${
                                                    data.domain === d ? 'border-amber-700 ring-2 ring-amber-500/40' : 'border-amber-400/50'
                                                }`}
                                            >
                                                <div className="text-2xl text-center mb-1">
                                                    {d === 'deadlands' && '💀'}
                                                    {d === 'drylands' && '🌵'}
                                                    {d === 'greenlands' && '🌿'}
                                                    {d === 'icelands' && '❄️'}
                                                    {d === 'sandlands' && '🏜️'}
                                                </div>
                                                <span className="block text-xs text-amber-900 text-center capitalize">{d}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Tile selection */}
                            {step === 2 && data.domain && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-amber-900">
                                            Choose a Tile in the {data.domain.charAt(0).toUpperCase() + data.domain.slice(1)}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setStep(1)}
                                            className="cursor-pointer text-amber-800 hover:text-amber-900 text-sm"
                                        >
                                            ← Back
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-60 overflow-auto pr-1">
                                        {(() => {
                                            const tiles = listTilesByDomain(data.domain);
                                            if (import.meta.env.DEV) {
                                                // eslint-disable-next-line no-console
                                                console.debug('[ClaimHexModal] tiles for', data.domain, tiles);
                                            }
                                            return tiles;
                                        })().length === 0 && (
                                            <div className="col-span-full text-amber-800/70 text-sm italic">No tiles available. Add images under resources/images/{data.domain}/</div>
                                        )}
                                        {listTilesByDomain(data.domain).map(({ filename, url }) => (
                                            <button
                                                key={filename}
                                                type="button"
                                                onClick={() => { setData('tile_image', filename); setStep(3); }}
                                                className={`cursor-pointer relative rounded-lg border overflow-hidden ${
                                                    data.tile_image === filename ? 'border-amber-700 ring-2 ring-amber-500/40' : 'border-amber-400/50'
                                                }`}
                                                title={filename}
                                            >
                                                <img src={url} alt={filename} className="w-full h-20 object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Confirm and claim */}
                            {step === 3 && data.domain && data.tile_image && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-amber-900">Domain: <span className="capitalize font-medium">{data.domain}</span></div>
                                        <button
                                            type="button"
                                            onClick={() => setStep(1)}
                                            className="cusor-pointer text-sm text-amber-800 hover:text-amber-900"
                                        >
                                            Change tile →
                                        </button>
                                    </div>
                                    <div>
                                        <div className="text-sm text-amber-900 mb-2">
                                            {keepExisting ? 'Pre-configured Tile' : 'Selected Tile Preview'}
                                        </div>
                                        {(() => {
                                            const url = getTileUrl(data.domain!, data.tile_image!);
                                            return (
                                                <div className="inline-block rounded-lg border border-amber-400/60 overflow-hidden shadow-sm">
                                                    {url ? (
                                                        <img
                                                            src={url}
                                                            alt={`Selected tile ${data.tile_image} in ${data.domain}`}
                                                            className="w-28 h-28 object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-28 h-28 bg-white/50 flex items-center justify-center text-amber-800 text-xs italic">
                                                            Preview unavailable
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {/* GM-only controls */}
                                    {isGm && (
                                        <div className="mt-2 space-y-3 border-t border-amber-800/20 pt-3">
                                            <p className="text-xs font-medium text-purple-700 uppercase tracking-wider mb-2">GM Settings</p>

                                            {/* Hex Type Selection */}
                                            <div className="space-y-2">
                                                <p className="text-sm font-medium text-amber-900">Hex Type</p>

                                                {/* Claimable by Players */}
                                                <label className="flex items-start gap-3 p-2 rounded-lg border border-amber-400/30 bg-white/30 hover:bg-white/50 cursor-pointer transition-colors">
                                                    <input
                                                        type="radio"
                                                        name="hex_type"
                                                        className="mt-1"
                                                        checked={data.is_claimable && !data.is_open_hex}
                                                        onChange={() => {
                                                            setData('is_claimable', true);
                                                            setData('is_open_hex', false);
                                                            setData('open_type', 'default');
                                                        }}
                                                    />
                                                    <div>
                                                        <span className="text-sm font-medium text-amber-900">Claimable by Players</span>
                                                        <p className="text-xs text-amber-800/70">Configure this hex for players to claim later. You will not own it.</p>
                                                    </div>
                                                </label>

                                                {/* Open Hex */}
                                                <label className="flex items-start gap-3 p-2 rounded-lg border border-amber-400/30 bg-white/30 hover:bg-white/50 cursor-pointer transition-colors">
                                                    <input
                                                        type="radio"
                                                        name="hex_type"
                                                        className="mt-1"
                                                        checked={data.is_open_hex && data.open_type === 'default'}
                                                        onChange={() => {
                                                            setData('is_claimable', false);
                                                            setData('is_open_hex', true);
                                                            setData('open_type', 'default');
                                                        }}
                                                    />
                                                    <div>
                                                        <span className="text-sm font-medium text-amber-900">Open Hex</span>
                                                        <p className="text-xs text-amber-800/70">A shared hex for visual/narrative purposes. No resources, cannot be claimed.</p>
                                                    </div>
                                                </label>

                                                {/* Enclave */}
                                                <label className="flex items-start gap-3 p-2 rounded-lg border border-purple-400/40 bg-purple-50/30 hover:bg-purple-50/50 cursor-pointer transition-colors">
                                                    <input
                                                        type="radio"
                                                        name="hex_type"
                                                        className="mt-1"
                                                        checked={data.is_open_hex && data.open_type === 'enclave'}
                                                        onChange={() => {
                                                            setData('is_claimable', false);
                                                            setData('is_open_hex', true);
                                                            setData('open_type', 'enclave');
                                                        }}
                                                    />
                                                    <div>
                                                        <span className="text-sm font-medium text-purple-800">Enclave</span>
                                                        <p className="text-xs text-purple-700/70">A special shared hex with community features. Functions like an Open Hex with additional capabilities.</p>
                                                    </div>
                                                </label>

                                                {/* Enclave Owner Name - shown when Enclave is selected */}
                                                {data.is_open_hex && data.open_type === 'enclave' && (
                                                    <div className="ml-6 mt-2">
                                                        <label htmlFor="owner_name" className="block text-sm font-medium text-purple-800 mb-1">
                                                            Enclave Name (Optional)
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="owner_name"
                                                            value={data.owner_name}
                                                            onChange={(e) => setData('owner_name', e.target.value)}
                                                            className="w-full px-3 py-2 bg-white/60 border border-purple-400/60 rounded-lg text-purple-900 placeholder-purple-700/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                                            placeholder="e.g., The Whispering Woods Guild"
                                                        />
                                                        <p className="text-xs text-purple-700/60 mt-1">
                                                            Display name shown instead of owner username
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Standard (GM claims it) */}
                                                <label className="flex items-start gap-3 p-2 rounded-lg border border-amber-400/30 bg-white/30 hover:bg-white/50 cursor-pointer transition-colors">
                                                    <input
                                                        type="radio"
                                                        name="hex_type"
                                                        className="mt-1"
                                                        checked={!data.is_claimable && !data.is_open_hex}
                                                        onChange={() => {
                                                            setData('is_claimable', false);
                                                            setData('is_open_hex', false);
                                                            setData('open_type', 'default');
                                                        }}
                                                    />
                                                    <div>
                                                        <span className="text-sm font-medium text-amber-900">Claim as GM</span>
                                                        <p className="text-xs text-amber-800/70">Claim this hex for yourself with full ownership.</p>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Error message (general) */}
                            {errors.q && (
                                <p className="text-sm text-red-700">{errors.q}</p>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-5 py-2.5 bg-amber-200/60 hover:bg-amber-200 text-amber-900 rounded-lg transition-colors border border-amber-400/50"
                                >
                                    Withdraw
                                </button>
                                <button
                                    type="submit"
                                    disabled={processing || !data.domain || !data.tile_image}
                                    className="flex-1 px-5 py-2.5 bg-gradient-to-br from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 disabled:from-amber-700 disabled:to-amber-800 disabled:cursor-not-allowed rounded-lg font-medium transition-all shadow-lg border border-amber-500/30 text-white"
                                >
                                    {/* Button text changes based on whether GM is configuring for players or claiming */}
                                    {processing
                                        ? (isGm && data.is_claimable ? 'Configuring…' : 'Proclaiming…')
                                        : (isGm && data.is_claimable ? 'Configure for Players' : 'Proclaim Ownership')
                                    }
                                </button>
                            </div>
                        </form>

                        {/* Bottom flourish */}
                        <div className="flex items-center justify-center gap-2 mt-6">
                            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-800/30" />
                            <span className="text-amber-800/30 text-sm">❧</span>
                            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-800/30" />
                        </div>
                    </div>
                </div>

                {/* Shadow beneath the parchment */}
                <div
                    className="absolute -bottom-2 left-4 right-4 h-4 rounded-full"
                    style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 70%)' }}
                />
            </div>
        </div>
    );
}
